// Shadow Network — Personalized feed (Following + Discover) with infinite scroll.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, TrendingUp, Users } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tShadow } from "../i18n/shadow-social";
import { UserAvatar } from "../components/shadow/UserAvatar";
import { AnalysisSocialBar } from "../components/shadow/AnalysisSocialBar";

import API from "../lib/api";
const PAGE_SIZE = 12;

const ShadowFeedPage = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [tab, setTab] = useState(user ? "following" : "discover");
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const sentinelRef = useRef(null);

  // REBEL AI discovery banner — visible only to authenticated users who
  // don't yet follow @rebel_ai AND haven't dismissed it in localStorage.
  // Honest onboarding: 1 fetch, no pop-up, dismissible forever.
  const [showRebelAiBanner, setShowRebelAiBanner] = useState(false);
  useEffect(() => {
    if (!user) return;
    if (typeof window !== "undefined" && window.localStorage?.getItem("rebel_ai_banner_dismissed") === "1") return;
    let alive = true;
    axios.get(`${API}/shadow/social/profile/rebel_ai`, { withCredentials: true })
      .then(({ data }) => {
        if (!alive) return;
        if (data?.user && !data.is_following) setShowRebelAiBanner(true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  const followRebelAi = async () => {
    try {
      await axios.post(`${API}/shadow/social/follow`, { target_user_id: "system:rebel-ai" }, { withCredentials: true });
      setShowRebelAiBanner(false);
    } catch {
      // silent
    }
  };
  const dismissRebelAiBanner = () => {
    try { window.localStorage?.setItem("rebel_ai_banner_dismissed", "1"); } catch { /* ignore */ }
    setShowRebelAiBanner(false);
  };

  const reset = useCallback(() => {
    setItems([]);
    setCursor(null);
    setDone(false);
  }, []);

  // reload when tab switches
  useEffect(() => {
    reset();
  }, [tab, reset]);

  const fetchPage = useCallback(
    async (nextCursor) => {
      if (loading || done) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({ type: tab, limit: String(PAGE_SIZE) });
        if (nextCursor) params.append("cursor", nextCursor);
        const { data } = await axios.get(
          `${API}/shadow/social/feed?${params.toString()}`,
          { withCredentials: true }
        );
        const fresh = data.items || [];
        setItems((prev) => [...prev, ...fresh]);
        setCursor(data.next_cursor || null);
        if (!data.next_cursor || fresh.length === 0) setDone(true);
      } catch (e) {
        setDone(true);
      } finally {
        setLoading(false);
      }
    },
    [tab, loading, done]
  );

  // Initial fetch + on-tab-reset
  useEffect(() => {
    if (items.length === 0 && !done && !loading) fetchPage(null);
  }, [items.length, done, loading, fetchPage]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !done) fetchPage(cursor);
      },
      { rootMargin: "200px" }
    );
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [cursor, loading, done, fetchPage]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="feed-page">
      <header className="border-b border-white/5 bg-black/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 md:px-8">
          <Link
            to="/shadow"
            className="inline-flex items-center gap-2 text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Shadow</span>
          </Link>
          <h1 className="text-base font-bold uppercase tracking-[0.18em]">Feed</h1>
          <span className="w-20" />
        </div>
        {/* Tabs */}
        <div className="mx-auto flex max-w-3xl gap-2 px-4 pb-3 md:px-8">
          {user && (
            <button
              onClick={() => setTab("following")}
              data-testid="feed-tab-following"
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition-all ${
                tab === "following"
                  ? "border-amber-400/60 bg-amber-400/[0.12] text-amber-200"
                  : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white"
              }`}
            >
              <Users className="h-3.5 w-3.5" />
              {tShadow("feedSiguiendo", lang)}
            </button>
          )}
          <button
            onClick={() => setTab("discover")}
            data-testid="feed-tab-discover"
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition-all ${
              tab === "discover"
                ? "border-cyan-400/60 bg-cyan-400/[0.12] text-cyan-200"
                : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white"
            }`}
          >
            <TrendingUp className="h-3.5 w-3.5" />
            {tShadow("descubrir", lang)}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6 md:px-8">
        {showRebelAiBanner && (
          <div
            data-testid="rebel-ai-discovery-banner"
            className="mb-4 flex items-center gap-3 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] to-amber-500/[0.02] p-4"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-amber-500/40 bg-amber-500/[0.10] font-mono text-xs font-black text-amber-400">
              AI
            </div>
            <div className="flex-1">
              <p className="font-mono text-[11px] font-black uppercase tracking-wider text-amber-400">
                🏆 REBEL AI · System Curator
              </p>
              <p className="mt-0.5 text-xs leading-snug text-white/70">
                {lang === "en"
                  ? "Follow REBEL AI and never miss a setup that compresses ≥14 days. Max 3 posts/day, always with disclaimer."
                  : lang === "sk"
                  ? "Sleduj REBEL AI a nezmeškaj setup, ktorý sa komprimuje ≥14 dní. Max 3 posty/deň, vždy s upozornením."
                  : "Seguí a REBEL AI y no te pierdas los setups que se comprimen ≥14 días. 3 posts/día máximo, siempre con disclaimer."}
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-1.5">
              <button
                type="button"
                data-testid="rebel-ai-banner-follow"
                onClick={followRebelAi}
                className="rounded-md border border-amber-500/60 bg-amber-500/[0.15] px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-400 transition hover:bg-amber-500/[0.25]"
              >
                {lang === "en" ? "Follow" : lang === "sk" ? "Sledovať" : "Seguir"}
              </button>
              <button
                type="button"
                data-testid="rebel-ai-banner-dismiss"
                onClick={dismissRebelAiBanner}
                className="rounded-md px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-white/40 transition hover:text-white/70"
              >
                {lang === "en" ? "Dismiss" : lang === "sk" ? "Zavrieť" : "Ocultar"}
              </button>
            </div>
          </div>
        )}
        {items.length === 0 && !loading && done && (
          <div
            data-testid="feed-empty"
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-16 text-center text-white/55"
          >
            {tab === "following"
              ? lang === "en"
                ? "You're not following anyone yet. Switch to Discover to find traders."
                : lang === "sk"
                ? "Ešte nikoho nesleduješ. Prepni na Objaviť."
                : "Aún no sigues a nadie. Cambia a Descubrir para encontrar traders."
              : "—"}
          </div>
        )}

        <ul className="space-y-4">
          {items.map((a) => (
            <li
              key={a.id}
              data-testid={`feed-item-${a.id}`}
              className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]"
            >
              {a.author && (
                <div className="flex items-center gap-2 px-4 py-3">
                  <UserAvatar user={a.author} size="sm" linkToProfile />
                  {a.author.username ? (
                    <Link
                      to={`/shadow/perfil/${a.author.username}`}
                      className="text-sm font-semibold text-white transition-colors hover:text-amber-300"
                      data-testid={`feed-author-link-${a.author.username}`}
                    >
                      {a.author.name}
                    </Link>
                  ) : (
                    <span className="text-sm font-semibold text-white/85">
                      {a.author.name}
                    </span>
                  )}
                  {a.author.username && (
                    <Link
                      to={`/shadow/perfil/${a.author.username}`}
                      className="text-[11px] text-white/40 transition-colors hover:text-amber-300"
                    >
                      @{a.author.username}
                    </Link>
                  )}
                  {user && a.author.id && user.id !== a.author.id && (
                    <Link
                      to={`/shadow/mensajes/${a.author.id}`}
                      onClick={(e) => e.stopPropagation()}
                      title="Enviar mensaje privado"
                      data-testid={`feed-message-${a.id}`}
                      className="ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-[11px] text-white/55 transition-all hover:border-amber-400/50 hover:bg-white/[0.1] hover:text-amber-300"
                    >
                      💬
                    </Link>
                  )}
                </div>
              )}
              {(a.image_url || a.video_url) && (
                <div className="aspect-video w-full overflow-hidden bg-black">
                  <img
                    src={a.image_url || a.video_url}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="p-4">
                {a.post_type === "diploma" && a.diploma_meta && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/55 bg-amber-400/[0.14] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-amber-200">
                    🎓 RE₿EL Proof™ · Nivel {a.diploma_meta.nivel}
                  </span>
                )}
                <h3 className={`text-base font-bold text-white ${a.post_type === "diploma" ? "mt-2" : ""}`}>{a.title}</h3>
                {a.post_type !== "diploma" && (
                  <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-white/40">
                    {a.ticker || "—"} · {a.direction || "—"} · {a.timeframe || "—"}
                  </p>
                )}
                {a.description && (
                  <p className="mt-2 line-clamp-3 text-sm text-white/65">{a.description}</p>
                )}
                {a.post_type === "diploma" && a.diploma_meta && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`feed-diploma-cta-${a.id}`}>
                    <a
                      href={a.diploma_meta.basescan_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2 py-1 text-[10px] font-bold text-cyan-300 transition-all hover:bg-cyan-400/20"
                    >
                      ⛓ BaseScan
                    </a>
                    <a
                      href={a.diploma_meta.opensea_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-fuchsia-400/30 bg-fuchsia-400/10 px-2 py-1 text-[10px] font-bold text-fuchsia-300 transition-all hover:bg-fuchsia-400/20"
                    >
                      OpenSea
                    </a>
                    <Link
                      to={a.diploma_meta.verify_url}
                      className="inline-flex items-center gap-1 rounded-md border border-amber-400/40 bg-amber-400/10 px-2 py-1 text-[10px] font-bold text-amber-300 transition-all hover:bg-amber-400/20"
                    >
                      Verificar →
                    </Link>
                  </div>
                )}
                <AnalysisSocialBar
                  analysisId={a.id}
                  initialLikesCount={a.reaction_count || 0}
                  commentsCount={a.comment_count || 0}
                  sharesCount={a.shares_count || 0}
                />
              </div>
            </li>
          ))}
        </ul>

        {loading && (
          <p className="text-center text-white/40 py-6 text-xs uppercase tracking-[0.18em]">
            …
          </p>
        )}
        <div ref={sentinelRef} className="h-12" aria-hidden />
      </main>
    </div>
  );
};

export default ShadowFeedPage;
